import { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { COLLAB_TOOLS } from '../constants/collabTools';
import { ensureNcSession, getGatewayUserId } from '../lib/ncSso';
import {
  causeText, classifyEmbedError, detectFrameProblem, embedErrorReason, probeNcState, reauthGuard, relogin,
  reportEmbedAuthEvent,
} from '../lib/embedAuth';
import { COLORS } from '../utils/uiStyles';
import { applyNcFSkin } from '../utils/collabSkinNc';
import { withEulReul } from '../utils/koreanParticle';

const AUTH_PROBE_MIN_MS = 30000;   // 복귀 때 NC 세션 확인은 30초에 한 번까지

// 협업도구 단독 새 창(/collab-view/:key) — NC 앱을 풀스크린 iframe으로 임베드.
// 새 창이라도 같은오리진 SPA이므로 NC 상단 헤더 잡메뉴 정리(cleanupNcHeader)가 적용된다(비관리자).
// 시안 F NC 피부(applyNcFSkin)는 여기서 넣지 않는다 — onLoad 폴링이 관리자 포함 전원에게 따로 부른다.

function cleanupNcHeader(doc, info) {
  if (!doc || !doc.head || !info) return;
  if (!doc.getElementById('gw-nchdr-css')) {
    const st = doc.createElement('style');
    st.id = 'gw-nchdr-css';
    st.textContent =
      '#header .header-start > *:not(#gw-hdr-label){display:none !important}' +
      '#header .header-end > *:not(#notifications){display:none !important}';
    doc.head.appendChild(st);
  }
  const hs = doc.querySelector('#header .header-start');
  if (hs && !doc.getElementById('gw-hdr-label')) {
    const lab = doc.createElement('div');
    lab.id = 'gw-hdr-label';
    lab.innerHTML = (info.svg || '') + '<span>' + info.label + '</span>';
    const s = lab.style;
    s.display = 'flex'; s.alignItems = 'center'; s.gap = '6px'; s.color = 'var(--gw-nc-hdr-fg, #fff)';
    s.fontWeight = '650'; s.fontSize = '15px'; s.paddingLeft = '8px'; s.whiteSpace = 'nowrap'; s.cursor = 'default';
    hs.insertBefore(lab, hs.firstChild);
  }
}

export default function CollabViewPage() {
  const { key } = useParams();
  const tool = COLLAB_TOOLS.find((t) => t.key === key);
  const ref = useRef(null);
  const [src, setSrc] = useState('');
  const [loadErr, setLoadErr] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [booting, setBooting] = useState(false);
  const [bootAttempt, setBootAttempt] = useState(0);
  const [cause, setCause] = useState(null);   // 실패 원인(lib/embedAuth CAUSE_TEXT 키) — 카드의 원인 줄
  const timerRef = useRef(null);
  const reauthTriedRef = useRef(false);       // 이 화면에서 자동 재인증을 이미 했는가(1회뿐)
  const reauthPendingRef = useRef(false);     // 재인증 뒤 다시 불러오는 중이면 그 사유(다음 정상 load 가 회복)
  const probeAtRef = useRef(0);
  const srcRef = useRef('');
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'admin'; } catch (e) { return false; } })();

  useEffect(() => { if (tool) document.title = tool.label + ' — 아워박스'; }, [tool]);
  useEffect(() => { srcRef.current = src; }, [src]);

  useEffect(() => {
    if (!tool) return undefined;
    let alive = true;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setLoaded(false);
    setLoadErr(false);
    setCause(null);
    setSrc('');
    setBooting(true);
    Promise.resolve(ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: tool.url }))
      .then(() => {
        if (!alive) return;
        setBooting(false);
        setSrc(tool.url);
        timerRef.current = setTimeout(() => setLoadErr(true), 20000);   // 20s 내 미로드 → 에러카드
      })
      .catch((err) => {
        if (!alive) return;
        setBooting(false);
        setCause(classifyEmbedError(err));
        setLoadErr(true);
      });
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [tool, bootAttempt]);

  const failWith = (why) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setLoaded(false);
    setCause(why);
    setLoadErr(true);
  };

  // 자동 재인증 1회: 숨은 SSO 로 NC 쿠키를 되살린 뒤 iframe 을 도구 주소로 다시 보낸다.
  // 🔴 이 화면에서 이미 했거나 guard(60초·10분 3회·실패 뒤)가 막으면 하지 않는다(false) — 호출부가 원인 카드로 바꾼다.
  const reauthFrame = (reason) => {
    if (!tool || reauthTriedRef.current || !reauthGuard.tryAcquire('nextcloud')) return false;
    reauthTriedRef.current = true;
    reportEmbedAuthEvent('nextcloud', 'expired', reason);
    setLoaded(false);
    Promise.resolve(ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: tool.url }))
      .then(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setLoadErr(true), 20000);
        reauthPendingRef.current = reason;
        const iframe = ref.current;
        try { iframe.contentWindow.location.replace(tool.url); } catch (e) {
          try { if (iframe) iframe.src = tool.url; } catch (e2) { /* */ }
        }
      })
      .catch((err) => {
        reauthGuard.fail('nextcloud');
        reportEmbedAuthEvent('nextcloud', 'reauth_fail', embedErrorReason(err));
        failWith(classifyEmbedError(err));
      });
    return true;
  };

  // 복귀(탭 다시 보임) 때 NC 세션이 없으면 같은 길(재인증 1회)을 탄다. 정상이면 아무것도 하지 않는다.
  useEffect(() => {
    if (!tool || typeof document === 'undefined') return undefined;
    let timer = null;
    const run = () => {
      timer = null;
      if (document.visibilityState !== 'visible' || !srcRef.current) return;
      const now = Date.now();
      if (probeAtRef.current && now - probeAtRef.current < AUTH_PROBE_MIN_MS) return;
      probeAtRef.current = now;
      probeNcState().then((probe) => {
        if (probe.state === 'none') reauthFrame('visibility_check');
      }).catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(run, 300);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tool]);

  const onLoad = () => {
    // NC 세션 없으면 로그인 페이지가 보일 수 있다(또는 차단된 프레임·게이트웨이 로그인 화면).
    // → 자동 재인증 1회. 이미 했거나 못 하면 오류 카드에 원인을 띄운다.
    const problem = detectFrameProblem(ref.current, 'collabtools', { afterLoad: true });
    if (problem) {
      const why = problem === 'blocked_frame' ? 'blocked_frame' : (problem === 'gateway_login' ? 'gateway_session' : 'auth_expired');
      const tried = reauthTriedRef.current;
      if (reauthFrame(problem === 'gateway_login' ? 'gateway_session' : problem)) return;
      if (tried) {
        // 재인증 뒤에도 그대로다 — 자동으로 다시 시도하지 않는다(«다시 시도» 만 guard 를 푼다).
        reauthPendingRef.current = false;
        reauthGuard.fail('nextcloud');
        reportEmbedAuthEvent('nextcloud', 'reauth_fail', why === 'auth_expired' ? 'session_missing' : why);
      }
      failWith(tried || why !== 'auth_expired' ? why : 'cooldown');
      return;
    }
    try {
      const w = ref.current && ref.current.contentWindow;
      const p = (w && w.location && w.location.pathname) || '';
      const isLogin = /\/login(\/|$|\?)/.test(p) || /index\.php\/login/.test(p);
      if (isLogin) {
        failWith('auth_expired');
        return;
      }
    } catch (e) { /* cross-origin/일시적 — 무시 */ }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (reauthPendingRef.current) {
      // 회복했다 — 다음 만료(몇 시간 뒤)에도 자동 재인증 1회를 다시 쓸 수 있다(연속은 guard 의 60초·10분 3회가 막는다).
      const reason = reauthPendingRef.current;
      reauthPendingRef.current = false;
      reauthTriedRef.current = false;
      reauthGuard.succeed('nextcloud');
      reportEmbedAuthEvent('nextcloud', 'reauth_ok', reason);
    }
    setLoaded(true);
    setLoadErr(false);
    // NC 헤더 늦은 렌더 대비 폴링.
    // 시안 F NC 피부는 관리자 포함 전원(EmbedPage 폴링과 같다) · 머리 잡메뉴 정리는 비관리자만(관리자는 NC 메뉴를 쓴다).
    // 피부는 공통만(협업 도구 5종엔 드라이브 전용 규칙을 넣지 않는다) · applyNcFSkin 은 id 로 1개만 유지해 반복 호출해도 같다.
    if (tool) {
      const run = () => { try {
        const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document));
        if (d) { applyNcFSkin(d); if (!isAdmin) cleanupNcHeader(d, { label: tool.label, svg: tool.hdrSvg }); }
      } catch (e) { /* */ } };
      run();
      let k = 0; const iv = setInterval(() => { run(); if (++k > 85) clearInterval(iv); }, 70);
    }
  };
  const retry = () => {
    // 사용자가 누르는 «다시 시도» 만 자동 재인증 상한(guard)과 1회 표식을 푼다.
    reauthGuard.reset('nextcloud');
    reauthTriedRef.current = false;
    reauthPendingRef.current = false;
    setLoadErr(false);
    setLoaded(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    setBootAttempt((n) => n + 1);
  };

  if (!tool) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'var(--gw-app-h, 100dvh)', color: '#6b7280', fontFamily: 'var(--gw-font)', background: '#f8fafc', padding: '24px' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(15,23,42,.06)', textAlign: 'center' }}>알 수 없는 도구입니다.</div>
      </div>
    );
  }
  return (
    <div style={{ position: 'fixed', inset: 0 }}>
      {src && !loadErr && (
        <iframe ref={ref} src={src} title={tool.label} onLoad={onLoad} onError={() => setLoadErr(true)}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          allow="clipboard-read; clipboard-write; fullscreen" />
      )}
      {!loaded && !loadErr && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', fontFamily: 'var(--gw-font)', color: '#6b7280' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '18px 22px', boxShadow: '0 1px 3px rgba(15,23,42,.06)', fontSize: '13px' }}>{booting ? '드라이브 연결 중…' : `${tool.label} 불러오는 중…`}</div>
        </div>
      )}
      {loadErr && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', background: '#fff', fontFamily: 'var(--gw-font)', color: '#374151', textAlign: 'center', padding: '24px' }}>
          <div style={{ width: 'min(460px, 100%)', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '24px', boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>{withEulReul(tool.label)} 불러오지 못했어요</div>
            <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6, marginTop: '8px' }}>드라이브 연결에 실패했습니다. 잠시 후 다시 시도해 주세요.</div>
            {/* 원인 한 줄(lib/embedAuth CAUSE_TEXT). 20초 안에 안 떴을 때처럼 원인을 모르면 '알 수 없음' 으로 둔다. */}
            <div data-gw-embed-auth-cause={cause || 'unknown'} style={{ fontSize: '12.5px', color: '#9a3412', lineHeight: 1.6, marginTop: '6px' }}>{causeText(cause)}</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
              <button onClick={retry}
                style={{ padding: '8px 16px', background: COLORS.primary, color: '#fff', border: 'none', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>다시 시도</button>
              <button onClick={() => window.open('/drive/', '_blank', 'noopener')}
                style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>드라이브 열기</button>
              <button onClick={() => { relogin(); }}
                style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>다시 로그인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
